/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

// Insert any custom theme js here...

/* Rosie Sugden: remove embroidery without removing the underlying product. */
(() => {
  'use strict';
  const embroideryVariant = 55876319936896;
  const groupKey = '_customily-upsell-main-id';
  const addonKey = '_customily-upsell-id';
  const marker = 'rs-remove-embroidery';
  let busy = false;

  const isCharge = item => Number(item.variant_id || item.id) === embroideryVariant;
  const groupOf = item => (item.properties || {})[groupKey];
  function parentsFor(cart, item) {
    if (!isCharge(item)) return [item];
    const group = (item.properties || {})[addonKey];
    return group ? cart.items.filter(other => !isCharge(other) && groupOf(other) === group) : [];
  }
  function hasEmbroidery(cart, item) {
    if (isCharge(item)) return true;
    return Object.keys(item.properties || {}).some(key => !key.startsWith('_') && /embroider/i.test(key)) ||
      Boolean(groupOf(item) && cart.items.some(other => isCharge(other) && (other.properties || {})[addonKey] === groupOf(item)));
  }
  function plainProperties(item, charges) {
    const optionNames = charges.map(charge => (charge.properties || {})['_customily-option-name']).filter(Boolean);
    return Object.fromEntries([
      ...Object.entries(item.properties || {}).filter(([key]) =>
        !key.startsWith('_customily') && !optionNames.includes(key) && !/embroider/i.test(key)),
      // Shopify requires a non-empty properties object to clear existing properties.
      // Keep this converted line distinct from an existing plain line. Shopify
      // can otherwise merge them while replacing rather than adding quantities.
      ['_rosie-personalisation', 'none:' + item.key]
    ]);
  }
  async function request(path, body) {
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    // Use Customily's own integration marker for these coordinated updates.
    // Its normal interceptor rewrites property changes into quantity updates
    // and can reload the page halfway through removing a linked charge.
    const response = await fetch(root + path + '?customily_upsell', body === undefined ? {cache: 'no-store'} : {
      method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body)
    });
    const result = await response.json();
    if (!response.ok || result.status >= 400) throw new Error(result.description || 'The basket could not be updated.');
    return result;
  }
  function showMessage(message) {
    let target = document.getElementById('rs-embroidery-message');
    if (!target) {
      const anchor = document.querySelector('cart-items');
      if (!anchor) return;
      target = document.createElement('p');
      target.id = 'rs-embroidery-message';
      target.setAttribute('role', 'alert');
      anchor.before(target);
    }
    target.textContent = message;
  }
  async function removeEmbroidery(key) {
    if (busy) return;
    busy = true;
    document.querySelectorAll('.' + marker).forEach(button => {button.disabled = true;});
    let changed = false;
    try {
      const cart = await request('cart.js');
      const selected = cart.items.find(item => item.key === key);
      if (!selected) { window.location.reload(); return; }
      const parents = parentsFor(cart, selected);
      const groups = new Set(parents.map(groupOf).filter(Boolean));
      const charges = cart.items.filter(item => isCharge(item) &&
        (item.key === selected.key || groups.has((item.properties || {})[addonKey])));
      const updates = {};
      charges.forEach(charge => {
        const group = (charge.properties || {})[addonKey];
        const others = cart.items.filter(item => !isCharge(item) && group && groupOf(item) === group && !parents.some(parent => parent.key === item.key));
        const multiplier = Number((charge.properties || {})['_customily-upsell-multiplier']) || 1;
        updates[charge.key] = others.reduce((sum, item) => sum + item.quantity * multiplier, 0);
      });
      // Clear the instructions and Customily linkage first, so its synchronizer
      // cannot recreate the charge against a still-personalised product.
      for (const parent of parents) {
        changed = true;
        await request('cart/change.js', {id: parent.key, quantity: parent.quantity, properties: plainProperties(parent, charges)});
      }
      // Customily may already have removed an orphan while processing the change.
      const current = await request('cart.js');
      const remainingUpdates = Object.fromEntries(Object.entries(updates).filter(([chargeKey]) => current.items.some(item => item.key === chargeKey)));
      if (Object.keys(remainingUpdates).length) {
        await request('cart/update.js', {updates: remainingUpdates});
        changed = true;
      }
      let finalCart = await request('cart.js');
      // Some cart integrations also rewrite another line of the same variant.
      // Reconcile against the starting basket so removing personalisation never
      // changes the number of physical products the customer chose.
      const expected = cart.items.filter(item => !isCharge(item)).map(item => ({
        ...item,
        properties: parents.some(parent => parent.key === item.key) ? plainProperties(item, charges) : item.properties
      }));
      const findExpected = (snapshot, item) => snapshot.items.find(other =>
        !isCharge(other) && Number(other.variant_id || other.id) === Number(item.variant_id || item.id) &&
        Object.keys(other.properties || {}).length === Object.keys(item.properties || {}).length &&
        Object.entries(item.properties || {}).every(([name, value]) => (other.properties || {})[name] === value));
      const corrections = {};
      const missing = [];
      expected.forEach(item => {
        const currentItem = findExpected(finalCart, item);
        if (!currentItem) {
          const replacement = {id:item.variant_id || item.id,quantity:item.quantity,properties:item.properties || {}};
          if (item.selling_plan_allocation) replacement.selling_plan = item.selling_plan_allocation.selling_plan.id;
          missing.push(replacement);
        } else if (currentItem.quantity !== item.quantity) corrections[currentItem.key] = item.quantity;
      });
      if (Object.keys(corrections).length) await request('cart/update.js', {updates:corrections});
      if (missing.length) await request('cart/add.js', {items:missing});
      if (Object.keys(corrections).length || missing.length) finalCart = await request('cart.js');
      if (expected.some(item => !findExpected(finalCart, item) || findExpected(finalCart, item).quantity !== item.quantity)) {
        throw new Error('Please check your item quantities before checking out.');
      }
      if (finalCart.items.some(item => isCharge(item) && Object.prototype.hasOwnProperty.call(updates, item.key) && item.quantity !== updates[item.key])) {
        throw new Error('The embroidery charge could not be removed. Please try again.');
      }
      window.location.reload();
    } catch (error) {
      console.warn('Rosie embroidery removal:', error.message);
      const message = changed
        ? 'Your personalisation has been updated, but an embroidery charge may still need removing. Please check your basket and try again.'
        : 'We could not remove embroidery just now. Please try again.';
      if (changed) {
        try {sessionStorage.setItem('rs-embroidery-error', message);} catch (_) { /* Message is also displayed below. */ }
        showMessage(message);
        window.location.reload();
      } else {
        showMessage(message);
        busy = false;
        document.querySelectorAll('.' + marker).forEach(button => {button.disabled = false;});
      }
    }
  }
  function render(cart) {
    document.querySelectorAll('cart-item[key]').forEach(element => {
      const item = cart.items.find(entry => entry.key === element.getAttribute('key'));
      if (!item) return;
      let button = element.querySelector('.' + marker);
      if (!hasEmbroidery(cart, item)) {if (button) button.remove(); return;}
      if (!button) {
        const details = element.querySelector('cart-item-details');
        if (!details) return;
        button = document.createElement('button');
        button.type = 'button';
        button.className = marker;
        button.style.cssText = 'display:block;margin-top:0.65rem;padding:0;border:0;background:none;color:inherit;font:inherit;text-align:left;text-decoration:underline;cursor:pointer;';
        details.append(button);
      }
      const label = 'Remove Personalisation';
      if (button.textContent !== label) button.textContent = label;
      button.dataset.cartKey = item.key;
      button.disabled = busy;
    });
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('.' + marker);
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    removeEmbroidery(button.dataset.cartKey);
  }, true);
  document.addEventListener('submit', event => {
    if (busy && event.target.closest('interactive-cart, .cart')) event.preventDefault();
  }, true);
  let attempts = 0;
  function initialise() {
    const store = window.theme && window.theme.cart && window.theme.cart.store;
    if (!store) {if (++attempts < 100) setTimeout(initialise, 100); return;}
    // Customily hides all add-on buttons; this control also clears the linked
    // production instructions, so it must remain available on the charge row.
    if (document.head && !document.getElementById('rs-embroidery-controls-style')) {
      const style = document.createElement('style');
      style.id = 'rs-embroidery-controls-style';
      style.textContent = 'button.rs-remove-embroidery{visibility:visible!important}';
      document.head.append(style);
    }
    const update = () => render(store.getState().products);
    store.subscribe(update);
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {scheduled = false; update();});
    });
    document.querySelectorAll('interactive-cart').forEach(element => observer.observe(element, {childList:true, subtree:true}));
    update();
    try {
      const message = sessionStorage.getItem('rs-embroidery-error');
      if (message) {sessionStorage.removeItem('rs-embroidery-error'); showMessage(message);}
    } catch (_) { /* Storage may be disabled. */ }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, {once:true});
  else initialise();
})();
